import { isEqual } from 'lodash-unified'
import Node from './node'

import type { Nullable } from '@element-plus/utils'
import type {
  CascaderConfig,
  CascaderNodePathValue,
  CascaderNodeValue,
  CascaderOption,
} from './node'

export const flatNodes = (nodes: Node[], leafOnly: boolean) => {
  const res: Node[] = []
  const walk = (list: Node[]) => {
    for (const node of list) {
      if (node.isLeaf) {
        res.push(node)
      } else {
        !leafOnly && res.push(node)
        walk(node.children)
      }
    }
  }
  walk(nodes)
  return res
}

const pathKey = (path: CascaderNodePathValue) =>
  path.map((value) => `${typeof value}:${value}`).join('\0')

const valueKey = (value: CascaderNodeValue, level?: number) =>
  `${level ?? ''}:${typeof value}:${value}`

export default class Store {
  readonly nodes: Node[]
  readonly allNodes: Node[]
  readonly leafNodes: Node[]
  private readonly valueMap = new Map<string, Node>()
  private readonly leafValueMap = new Map<string, Node>()
  private readonly pathMap = new Map<string, Node>()
  private readonly sameNodeMap = new Map<string, Node>()

  constructor(data: CascaderOption[], readonly config: CascaderConfig) {
    const nodes = (data || []).map(
      (nodeData) => new Node(nodeData, this.config)
    )
    this.nodes = nodes
    this.allNodes = flatNodes(nodes, false)
    this.leafNodes = flatNodes(nodes, true)
    this.allNodes.forEach((node) => this.indexNode(node))
  }

  private indexNode(node: Node) {
    const key = valueKey(node.value)
    if (!this.valueMap.has(key)) this.valueMap.set(key, node)
    if (node.isLeaf && !this.leafValueMap.has(key)) {
      this.leafValueMap.set(key, node)
    }
    this.pathMap.set(pathKey(node.pathValues), node)
    this.sameNodeMap.set(valueKey(node.value, node.level), node)
  }

  getNodes() {
    return this.nodes
  }

  getFlattedNodes(leafOnly: boolean) {
    return leafOnly ? this.leafNodes : this.allNodes
  }

  appendNode(nodeData: CascaderOption, parentNode?: Node) {
    const node = parentNode
      ? parentNode.appendChild(nodeData)
      : new Node(nodeData, this.config)

    if (!parentNode) this.nodes.push(node)

    this.allNodes.push(node)
    node.isLeaf && this.leafNodes.push(node)
    this.indexNode(node)
  }

  appendNodes(nodeDataList: CascaderOption[], parentNode: Node) {
    nodeDataList.forEach((nodeData) => this.appendNode(nodeData, parentNode))
  }

  // when checkStrictly, leaf node first
  getNodeByValue(
    value: CascaderNodeValue | CascaderNodePathValue,
    leafOnly = false
  ): Nullable<Node> {
    if (!value && value !== 0) return null

    if (Array.isArray(value)) {
      return (
        this.pathMap.get(pathKey(value)) ||
        this.getFlattedNodes(leafOnly).find(
          (node) =>
            isEqual(node.value, value) || isEqual(node.pathValues, value)
        ) ||
        null
      )
    }

    const mapped = (leafOnly ? this.leafValueMap : this.valueMap).get(
      valueKey(value)
    )
    if (mapped) return mapped

    return (
      this.getFlattedNodes(leafOnly).find(
        (node) => isEqual(node.value, value) || isEqual(node.pathValues, value)
      ) || null
    )
  }

  getSameNode(node: Node): Nullable<Node> {
    if (!node) return null

    return (
      this.sameNodeMap.get(valueKey(node.value, node.level)) ||
      this.getFlattedNodes(false).find(
        ({ value, level }) => isEqual(node.value, value) && node.level === level
      ) ||
      null
    )
  }
}
